import { setFeatureSetId } from "@classdojo/log-client";
import { isEqual, partition } from "es-toolkit";
import { NOOP, WAITING_FOR_DEPENDENCIES } from "@web-monorepo/fetchers";
import { isProduction, isTesting } from "@web-monorepo/environment";
import { localStorage } from "@web-monorepo/safe-browser-storage";
import { useApiFeatureSwitchesFetcher } from "./useApiFeatureSwitchesFetcher";
// Imported from the subpath rather than the package barrel on purpose: `utils` is
// side-effect free (it only pulls in Sentry types), and going direct means the registration
// below still runs in suites whose vitest setup stubs the `@web-monorepo/telemetry` barrel.
import { setFeatureSwitchesSource } from "@web-monorepo/telemetry/utils";
import { useCallback, useEffect, useRef } from "react";
import type { FeatureSwitchName, FeatureSwitchVariants } from "./generated";

export type {
  FeatureSwitchName,
  FeatureSwitchVariants,
  FeatureSwitchVariantArray,
  StartableExperiment,
  FeatureSwitchDef,
  FeatureSwitchNameAndVariant,
  AudienceType,
  AppName,
} from "./generated";

// @fetcher-revisit:
// - clear local storage featureSetIdLocalStorageKey value on user logout

let FEATURE_SET_ID_LOCALSTORAGE_KEY: string | null = null;

//
// featureSetId holds some logic in API that seemed confusing at first, lets try brief how we use it here:
//
// Every time we call API it will generate a featureSetId, for analytics purposes.
// The featureSetId is generated based on the query string params (except for featureSetId itself) and
// the response values for the switches. API will return this featureSetId in the response so we can store
// it and use it in latest calls.
// So for instance whenever we call /featureSwtiches?parentLocale=en&parentSwitches=sw1&featureSetId=123
// API will create the hash again and if the hash matches with the one we sent (featureSetId)
// then it won’t add a new record in the DB, if it does not then API will add a new record for later usage on
// analytics (that’s why we set it in the log client in our side).
// API will never end in a wrong state, since it’s always generating the feature set id based on
// the params (hashing), but we do send the id on subsequent calls just to avoid going to the DB for
// saving the record over and over on each call for the same user and config.
//
// So basically from our side every time we get an answer back from our call to feature switches end
// point we do two important things, we set the featureSetId in the log client and we store it
// in local storage (for using it in the future calls to the end point, which happens when loading the page).
// For simplicity we read only once from the local storage, when loading the APP and then we use that
// featureSetId for our call for the life cycle of the app (until the user reloads).
// We do this because we actually never call /featureSwitches end point after we load the app, so it
// does not matter to try read the latest value from local storage (this allowed us to simplify the
// logic and avoid extra fetches, due to fetchers default functionality with the cache).
// If in the future we want to make extra calls to the feature switches endpoint during the usage of
// the app, we need to make sure we read from local storage always the latest value.
// Worth mentioning that if the user changes anything that affects the query string (like locale) in
// that case we do perform a new call since we actually changed something that will affect the
// result and the featureSetId.
let storedFeatureSetId: string | undefined = undefined;

// The static config for the switches.
//  - useBuildFeatureSwitchesQueryParamsHook: Check the docs below for more details
//  - useFeatureSwitchOverridesHook: hook to load any feature switch override values to use
let featureSwitchesConfiguration: {
  useBuildFeatureSwitchesQueryParamsHook: () => Record<string, unknown> | null;
  useFeatureSwitchOverridesHook: () => Record<string, unknown> | null | undefined;
  featureSwitchesFetcherQueryParams: string[];
} | null = null;

/**
 * Sets the configuration for loading and using feature switches.
 * It should be called during application startup, before any of the exported feature switch hooks are used.
 *
 * @param {Object} params
 * @param {string} params.featureSetIdLocalStorageKey
 *   The name of the key in the local storage for storing the featureSetId (not too relevant what key is picked here
 *
 * @param {Function} params.useBuildFeatureSwitchesQueryParamsHook
 *   Every frontend has different needs for fetching switches. Some will fetch by school or by teacher or by session
 *   (logged out). Only the consumer frontend knows what dependencies are required and how to load them. We expect
 *   consumers to provide a hook that will resolve its dependencies and return the query string params value to be
 *   used when calling the  feature switches API endpoint.
 *
 *   We expect the consumer to provide a hook that depending on the data found in the app will return the
 *   query string params for fetching the switches. For example:
 *
 *   - If the user has a session/parent then `useBuildFeatureSwitchesQueryParamsHook` should return:
 *
 *     {
 *       parentId: "12345",
 *       parentLocale: "en-US",
 *       parentSwitches: "Switch1,Switch2"
 *     }
 *
 *   - If the user is logged out `useBuildFeatureSwitchesQueryParamsHook` should return:
 *
 *     {
 *       sessionId: "12345",
 *       sessionSwitches: "Switch1"
 *     }
 *
 *   - If app is still waiting on some data dependencies to load, it should return `null`
 *
 *   - If there are no feature switches to fetch, it should return `""` (empty string)
 *
 * @param {Function} [params.useFeatureSwitchOverridesHook]
 *   Optional hook that returns a key/value object with any feature switch values that should override the
 *   API returned values. Any value returned from the API that is not overriden will be kept.
 *
 * @param {string[]} [params.featureSwitchesFetcherQueryParams]
 *   Name or the query string parameters to be used by the featureSwitchesFetcher
 */
export function setFeatureSwitchesConfig({
  featureSetIdLocalStorageKey,
  useBuildFeatureSwitchesQueryParamsHook,
  useFeatureSwitchOverridesHook,
  featureSwitchesFetcherQueryParams,
}: {
  featureSetIdLocalStorageKey: string;
  useBuildFeatureSwitchesQueryParamsHook: () => Record<string, unknown> | null;
  useFeatureSwitchOverridesHook?: () => Record<string, unknown> | null | undefined;
  featureSwitchesFetcherQueryParams: string[];
}) {
  if (!useBuildFeatureSwitchesQueryParamsHook) {
    throw new Error("You must specify a function for `useBuildFeatureSwitchesQueryParamsHook` configuration option");
  }

  featureSwitchesConfiguration = {
    useBuildFeatureSwitchesQueryParamsHook,
    useFeatureSwitchOverridesHook: useFeatureSwitchOverridesHook || (() => undefined),
    featureSwitchesFetcherQueryParams,
  };
  FEATURE_SET_ID_LOCALSTORAGE_KEY = featureSetIdLocalStorageKey;

  // Sending empty string so we still fetch even if we don't have one
  storedFeatureSetId = localStorage.getItem(FEATURE_SET_ID_LOCALSTORAGE_KEY) || "";
}

// fetcher to make API call - it uses a `query` parameter which is a string
// containing all the serialized query string key/value parameters we need to send,
// in addition to `featureSetId`

//
// Public API
//

/**
 * Ensures all the configured feature switch values are fetched from the API.
 * You can use this hook to pre-load the cache on application startup.
 * It returns an object with `{ ready: bool, data: object }`
 */
export const useEnsureFeatureSwitches = () => {
  if (!featureSwitchesConfiguration) {
    throw new Error("You must call `setFeatureSwitchesConfig` to initialize feature switches first");
  }

  const { useBuildFeatureSwitchesQueryParamsHook } = featureSwitchesConfiguration;

  // get query string send to API
  const queryStringParams = useBuildFeatureSwitchesQueryParamsHook();
  if (queryStringParams !== null && !(typeof queryStringParams === "object")) {
    // invalid type returned from useBuildFeatureSwitchesQueryParamsHook
    throw new Error(
      `\`useBuildFeatureSwitchesQueryParamsHook\` returned an invalid value type. ` +
        `Make sure it returns an object or \`null\` if app is still waiting for dependencies. ` +
        `return=[${JSON.stringify(queryStringParams)}]`,
    );
  }

  const isWaitingForDependencies = queryStringParams === null;
  const hasNoFeatureSwitches = queryStringParams !== null && Object.keys(queryStringParams).length === 0;

  const params = isWaitingForDependencies
    ? WAITING_FOR_DEPENDENCIES
    : hasNoFeatureSwitches
      ? NOOP
      : {
          featureSetId: storedFeatureSetId,
          ...queryStringParams,
        };
  // `params` can be the sentinel constants `WAITING_FOR_DEPENDENCIES` / `NOOP` (handled at runtime
  // by the fetcher infrastructure) or the typed query-string object — the fetcher's declared
  // parameter type doesn't include the sentinels, so we name the cast target precisely.
  const featureSwitchesResult = useApiFeatureSwitchesFetcher(
    params as Parameters<typeof useApiFeatureSwitchesFetcher>[0],
  );

  // @fetchers-revisit: add some logging to let developers know that feature switches
  // are refetching, which will cause the application component tree to be re-mounted,
  // and can cause some undesired behavior

  // in case there are no feature switches to load, we return empty object
  const data = hasNoFeatureSwitches ? { holdout: {} } : featureSwitchesResult.data;
  const { holdout, ...rest } = data || {};
  if (featureSwitchesResult.data?.featureSetId) {
    setFeatureSetId(featureSwitchesResult.data.featureSetId);

    if (FEATURE_SET_ID_LOCALSTORAGE_KEY) {
      localStorage.setItem(FEATURE_SET_ID_LOCALSTORAGE_KEY, featureSwitchesResult.data.featureSetId);
    }
  }

  return {
    data: data === undefined ? undefined : rest,
    holdoutData: holdout,
    ready: params === NOOP || (!featureSwitchesResult.isInitialLoading && params !== WAITING_FOR_DEPENDENCIES),
    error: featureSwitchesResult.error,
    isWaitingForDependencies,
  };
};

/**
 * Gets the values for all the feature switches, including any overridden values.
 *
 * We use the `useFetched*` convention here. This means that by convention ApplicationContainer
 * will guarantee that whatever information this fetcher relies on, will be initialized
 * before rendering any children. The reasoning behind is that some info (like session,
 * userConfig, teacher, etc) should always be present the moment after we load the app.
 * By loading this information at initialization time, before rendering any screen, we can now
 * consume it without needing to add null checkers.
 * Is not an ideal solution but avoids unnecessary null checks on fetchers that we always want to
 * be present after login. We might rework this with suspense in the short term.
 */
declare global {
  interface Window {
    localSwitchOverrides?: Record<string, string>;
  }
}

// Apply feature switch overrides from URL query parameters.
// Supports both regular query params (?web_foo=on) and hash-based routing (#/?web_foo=on).
// Feeds overrides through the devTools system so they show up in the panel, can be cleared
// from there, and follow the same override pipeline as manual devTools changes.
export function _applyFeatureSwitchQueryParams(
  location: { search: string; hash: string } = window.location,
): Record<string, string> {
  const params = new URLSearchParams(location.search);
  const hashIndex = location.hash.indexOf("?");
  if (hashIndex !== -1) {
    const hashParams = new URLSearchParams(location.hash.substring(hashIndex));
    for (const [key, value] of hashParams) {
      params.set(key, value);
    }
  }

  const overrides: Record<string, string> = {};
  for (const [key, value] of params) {
    if (key.startsWith("web_")) {
      overrides[key] = value;
    }
  }

  if (Object.keys(overrides).length > 0) {
    // Enable devTools so overrides flow through the standard pipeline and the
    // panel is visible (letting users see and clear URL-driven overrides).
    localStorage.setItem("devTools.enable", "true");

    // Merge with existing devTools overrides (URL params take priority)
    const raw = localStorage.getItem("devTools.featureSwitchOverrides");
    const existing = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    const merged = { ...existing, ...overrides };
    localStorage.setItem("devTools.featureSwitchOverrides", JSON.stringify(merged));
  }

  return overrides;
}

if (typeof window !== "undefined") {
  try {
    _applyFeatureSwitchQueryParams();
    // eslint-disable-next-line no-catch-all/no-catch-all
  } catch {
    // Silently ignore all errors (URL parsing, localStorage access, malformed JSON)
  }
}

/* Temporary solution to allow local overrides of feature switches to be cached,
 * should likely be removed once we integrate with a state management library (zustand, valtio, etc).
 */

// eslint-disable-next-line no-unassigned-vars
let globalDataReference: ReturnType<typeof useEnsureFeatureSwitches>["data"] | undefined;
// eslint-disable-next-line no-unassigned-vars
let globalOverrideReference: Record<string, string> | undefined;
let globalResolvedFeatureSwitchesCache: Record<string, string> | undefined;

const CONFLICTING_FEATURE_SWITCH_VARIANT_OVERRIDE = "conflicting_feature_switch_variant_override" as const;

/**
 * The switches resolved by the most recent `useFetchedFeatureSwitchesWithOverrides()` call,
 * or `undefined` before any have resolved.
 *
 * Read-only accessor for telemetry: this package no longer writes to Sentry's scope itself.
 * `featureSwitchBeforeSend` in `@web-monorepo/telemetry` samples this when an event is sent,
 * so switch data lands on every capture path instead of only on events that happen to follow
 * a render that read a switch. See WEBP-544.
 */
export function getResolvedFeatureSwitches(): Record<string, string> | undefined {
  return globalResolvedFeatureSwitchesCache;
}

// Registered at module load rather than imported by telemetry: telemetry cannot depend on
// this package without closing a dependency cycle (feature-switches -> telemetry, and
// feature-switches -> fetchers -> telemetry).
setFeatureSwitchesSource(getResolvedFeatureSwitches);

// eslint-disable-next-line complexity
export const useFetchedFeatureSwitchesWithOverrides = <T extends string>(): Record<T, string> => {
  const { data, ready, isWaitingForDependencies, holdoutData } = useEnsureFeatureSwitches();
  const useFeatureSwitchOverridesHook = featureSwitchesConfiguration?.useFeatureSwitchOverridesHook;
  const featureSwitchOverrides = useFeatureSwitchOverridesHook?.() || {};

  if (
    globalDataReference === data &&
    isEqual(globalOverrideReference, featureSwitchOverrides) &&
    globalResolvedFeatureSwitchesCache
  ) {
    return globalResolvedFeatureSwitchesCache;
  }

  if (!isProduction && !ready && !isWaitingForDependencies) {
    // console.warn this instead of throwing an error because we were seeing some issues
    // when reloading feature switches, for example, when we change the teacher language multiple times.
    // This shouldn't happen anyway, since it is part of the general one time app setup,
    // so it is only intended as a dev helper so we don't forget to do it.
    // eslint-disable-next-line no-console
    console.warn(
      `Feature Switches are not loaded yet! Please call \`useEnsureFeatureSwitches()\` in ApplicationContainer.`,
    );
  }

  const defaultValue = {} as Record<T, string>;

  if (data) {
    // flattens all the feature switches returned by api into an object with a single level
    // So, if API returns: { parent: { switch1: "on" }, session: { switch2: "off" } }
    // allSwitches will be { switch1: "on", switch2: "off" }. This prefers values that are not "off"
    const allSwitches = Object.values(data).reduce<Record<T, string>>((switches, group) => {
      if (typeof group !== "object") return switches;

      // We do this to ensure that "off" switches are always overwritten with a value other than "off", if there are duplicates.
      // This is necessary because some switches are both school and entity level, and we want to prefer the user being in a
      // switch vs out of a switch.
      const [offSwitchesEntries, otherSwitchesEntries] = partition(
        Object.entries(group),
        ([, value]) => value === "off",
      );
      // TODO: it looks like these are either strings or objects. The "as" here seems to be covering up a real danger.
      const offSwitches = Object.fromEntries(offSwitchesEntries) as Record<T, string>;
      const otherSwitches = Object.fromEntries(otherSwitchesEntries) as Record<T, string>;

      // If a user has multiple variants for the same feature switch, we don't want to randomly select one
      // Here we mark a conflict, and after the reduce, resolve all conflicting variants to "off"
      for (const nonOffSwitch in otherSwitches) {
        if (
          switches &&
          switches[nonOffSwitch] &&
          switches[nonOffSwitch] !== "off" &&
          switches[nonOffSwitch] !== otherSwitches[nonOffSwitch]
        ) {
          otherSwitches[nonOffSwitch] = CONFLICTING_FEATURE_SWITCH_VARIANT_OVERRIDE;
        }
      }

      return { ...offSwitches, ...switches, ...otherSwitches };
    }, defaultValue);

    const allSwitchesWithConflictsRemoved = Object.fromEntries(
      Object.entries(allSwitches).map(([k, v]) => [k, v === CONFLICTING_FEATURE_SWITCH_VARIANT_OVERRIDE ? "off" : v]),
    ) as Record<T, string>;

    const allSwitchesWithHoldout = { ...allSwitchesWithConflictsRemoved };
    if (holdoutData) {
      for (const [k, v] of Object.entries(holdoutData)) {
        if (v.status === "shipped" && v.shippedVariant) {
          allSwitchesWithHoldout[k as T] = v.shippedVariant;
        }
      }
    }

    const allSwitchesWithOverrides = featureSwitchOverrides
      ? { ...allSwitchesWithHoldout, ...featureSwitchOverrides }
      : allSwitchesWithHoldout;

    // fetchers-revisit: we need to look into how to provide better support for testing
    // allow initial overrides to be set on window for E2E tests
    const allSwitchesWithTestOverrides = window.localSwitchOverrides
      ? { ...allSwitchesWithOverrides, ...window.localSwitchOverrides }
      : allSwitchesWithOverrides;

    // Deliberately no Sentry write here: `featureSwitchBeforeSend` in
    // `@web-monorepo/telemetry` reads the cache below at send time, so tagging no longer
    // depends on which components have rendered. See WEBP-544.
    return (globalResolvedFeatureSwitchesCache = allSwitchesWithTestOverrides);
  }

  // Not cached: an empty default means "switches have not resolved yet", and
  // `getResolvedFeatureSwitches()` has to keep reporting `undefined` so telemetry can tell
  // that apart from an app that genuinely resolved zero switches. See WEBP-544.
  return defaultValue;
};

/**
 * Gets the value for the specified feature switch.
 *
 * We use the `useFetched*` convention here. This means that by convention ApplicationContainer
 * will guarantee that whatever information this fetcher relies on, will be initialized
 * before rendering any children. The reasoning behind is that some info (like session,
 * userConfig, teacher, etc) should always be present the moment after we load the app.
 * By loading this information at initialization time, before rendering any screen, we can now
 * consume it without needing to add null checkers.
 * Is not an ideal solution but avoids unecessary null checks on fetchers that we always want to
 * be present after login. We might rework this with suspense in the short term.
 *
 * @param {string} switchName
 * @returns {string}
 */
export const useFetchedFeatureSwitch = <TName extends FeatureSwitchName>(
  switchName: TName,
): FeatureSwitchVariants<TName> => {
  const data = useFetchedFeatureSwitchesWithOverrides<string>();

  // cast safe: "off" is always a valid variant per registry generation
  return (data && data[switchName] ? data[switchName] : "off") as FeatureSwitchVariants<TName>;
};

/**
 * Checks if the specified feature switch is currently `on`.
 * The value for `switchName` must be the same as one of the values
 * specified in `featureSwitches` when calling `setFeatureSwitchesConfig`
 *
 * We use the `useFetched*` convention here. This means that by convention ApplicationContainer
 * will guarantee that whatever information this fetcher relies on, will be initialized
 * before rendering any children. The reasoning behind is that some info (like session,
 * userConfig, teacher, etc) should always be present the moment after we load the app.
 * By loading this information at initialization time, before rendering any screen, we can now
 * consume it without needing to add null checkers.
 * Is not an ideal solution but avoids unecessary null checks on fetchers that we always want to
 * be present after login. We might rework this with suspense in the short term.
 *
 * @param {string} switchName
 * @returns {boolean}
 */
export const useFetchedIsFeatureSwitchOn = (switchName: FeatureSwitchName) => {
  const switchValue = useFetchedFeatureSwitch(switchName);

  if (switchValue !== "off" && switchValue !== "on") {
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.warn(
        `Called \`useFetchedIsFeatureSwitchOn()\` with a Feature Switch that is not an on/off value! ` +
          `switchName=[${switchName}] switchValue=[${switchValue}]`,
      );
    }
  }

  return switchValue === "on";
};

/**
 * Checks if the feature switch value matches the specified variant value.
 * The value for `switchName` must be the same as one of the values
 * specified in `featureSwitches` when calling `setFeatureSwitchesConfig`
 *
 * We use the `useFetched*` convention here. This means that by convention ApplicationContainer
 * will guarantee that whatever information this fetcher relies on, will be initialized
 * before rendering any children. The reasoning behind is that some info (like session,
 * userConfig, teacher, etc) should always be present the moment after we load the app.
 * By loading this information at initialization time, before rendering any screen, we can now
 * consume it without needing to add null checkers.
 * Is not an ideal solution but avoids unecessary null checks on fetchers that we always want to
 * be present after login. We might rework this with suspense in the short term.
 *
 * @param {string} switchName
 * @param {string} variantValue
 * @returns {boolean}
 */
export const useFetchedIsFeatureSwitchVariant = (switchName: FeatureSwitchName, variantValue: string) => {
  const switchValue = useFetchedFeatureSwitch(switchName);
  return switchValue === variantValue;
};

/**
 * Checks if the feature switch value matches the specified variant value.
 * The value for `switchName` must be the same as one of the values
 * specified in `featureSwitches` when calling `setFeatureSwitchesConfig`
 *
 * We use the `useFetched*` convention here. This means that by convention ApplicationContainer
 * will guarantee that whatever information this fetcher relies on, will be initialized
 * before rendering any children. The reasoning behind is that some info (like session,
 * userConfig, teacher, etc) should always be present the moment after we load the app.
 * By loading this information at initialization time, before rendering any screen, we can now
 * consume it without needing to add null checkers.
 * Is not an ideal solution but avoids unecessary null checks on fetchers that we always want to
 * be present after login. We might rework this with suspense in the short term.
 *
 * @param {string} switchName
 * @param {Array<string>} variantsValue
 * @returns {boolean}
 */
export const useFetchedIsFeatureSwitchInAnyVariant = (switchName: FeatureSwitchName, variantsValue: string[]) => {
  const switchValue = useFetchedFeatureSwitch(switchName);
  return variantsValue.some((variantValue) => switchValue === variantValue);
};

/**
 * Checks if the feature switch value matches by regextp the specified variant value.
 * This is a new addition to support "sub-variants", that is multiple varianst with the
 * same name, but with some subname that we are using internally for analytics.
 * The value for `switchName` must be the same as one of the values
 * specified in `featureSwitches` when calling `setFeatureSwitchesConfig`
 *
 * We use the `useFetched*` convention here. This means that by convention ApplicationContainer
 * will guarantee that whatever information this fetcher relies on, will be initialized
 * before rendering any children. The reasoning behind is that some info (like session,
 * userConfig, teacher, etc) should always be present the moment after we load the app.
 * By loading this information at initialization time, before rendering any screen, we can now
 * consume it without needing to add null checkers.
 * Is not an ideal solution but avoids unecessary null checks on fetchers that we always want to
 * be present after login. We might rework this with suspense in the short term.
 *
 * @param {string} switchName
 * @param {string} variantValue
 * @returns {boolean}
 */
export const useFetchedIsFeatureSwitchVariantPrefix = (switchName: FeatureSwitchName, variantValue: string) => {
  const switchValue = useFetchedFeatureSwitch(switchName);

  const regexp = new RegExp(`^${variantValue}(-.+){0,1}$`, "g");
  return regexp.test(switchValue);
};

export const useFetchedIsFeatureSwitchVariantPrefixInAudience = (
  switchName: FeatureSwitchName,
  variants: string | string[],
) => {
  if (!Array.isArray(variants)) {
    variants = [variants];
  }

  const switchValue = useFetchedFeatureSwitch(switchName);

  const ret = variants.some((variant) => {
    const regexp = new RegExp(`^${variant}(-.+){0,1}$`, "g");
    return regexp.test(switchValue);
  });

  return ret;
};

export const isHoldoutVariant = (variant: string) => {
  return ["baseline", "shipped_improvements"].includes(variant);
};

// Start experiment hooks
function _startExperiment<T extends FeatureSwitchName>({
  experiment,
  data,
  holdoutData,
  isInAudience,
  logStartExperiment,
  logEvent,
  manualOverride,
}: {
  experiment: T;
  data: Record<T, string>;
  holdoutData: Record<
    string,
    { status: "shipped"; holdoutName: string; shippedVariant: string } | { status: "active"; holdoutName: string }
  >;
  isInAudience: boolean;
  logStartExperiment: (experiment: string) => void;
  logEvent: (event: { eventName: string; eventValue: string; metadata: Record<string, string> }) => void;
  manualOverride: string | undefined;
}): string {
  const handleError = (msg: string) => {
    if (!isProduction) {
      throw new Error(msg);
    } else {
      // eslint-disable-next-line no-console
      console.error(msg);
    }
    return "off";
  };
  if (!experiment) return handleError(`Invalid experiment name: ${experiment}`);
  if (Object.entries(data).length === 0) return "off";

  if (!isInAudience) return "off";
  const holdoutDataForExperiment = holdoutData?.[experiment];
  const experimentVariant = data[experiment];
  // if no holdout data for experiment, log start experiment and return experiment variant
  if (!holdoutDataForExperiment) {
    if (!experimentVariant) {
      return handleError(`Invalid experiment name: ${experiment}`);
    }
    if (experimentVariant === "off") return "off";
    logStartExperiment(experiment);
    return experimentVariant;
  }

  // if holdout data for experiment is shipped, log start holdout and return shipped variant
  if (holdoutDataForExperiment.status === "shipped") {
    const { shippedVariant, holdoutName } = holdoutDataForExperiment;
    const holdoutVariant = data[holdoutName as T];
    if (isHoldoutVariant(holdoutVariant)) {
      logEvent({
        eventName: `holdout.started_for_experiment`,
        eventValue: experiment,
        metadata: {
          holdout: holdoutName,
          experiment,
          shippedVariant,
          holdoutVariant,
        },
      });
      logStartExperiment(holdoutName);
      // if user should not see the shipped variant (they're in holdout), return off
      if (holdoutVariant === "baseline") {
        return "off";
      }
    }

    // if user should see the shipped variant (shipped_improvements, off), return the shipped variant
    // this is equivalent to returning `experimentVariant` since it starts with the holdout value and is
    // overwritten by the DevTools override, but we use manualOverride do be explicit about the order of precedence.
    // and making sure that in production we always default to the shipped variant.
    return manualOverride || shippedVariant;
  }

  const { holdoutName } = holdoutDataForExperiment;
  const holdoutVariant = data[holdoutName as T];

  // exclude users in holdout from experiment
  if (isHoldoutVariant(holdoutVariant)) {
    return "off";
  }
  if (experimentVariant === "off") return "off";
  logStartExperiment(experiment);
  return experimentVariant;
}

export type StartExperimentWhenInAudienceOptions<T extends FeatureSwitchName> = {
  logStartExperiment: (experiment: string) => void;
  logEvent: (event: { eventName: string; eventValue: string; metadata: Record<string, string> }) => void;
  clientExcludeUser?: (data: Record<T, string>, experiment: T) => boolean;
};

export const useStartExperimentWithHoldout = <T extends FeatureSwitchName>(
  experiment: T,
  { isInAudience }: { isInAudience: boolean },
  options: StartExperimentWhenInAudienceOptions<T>,
) => {
  const data = useFetchedFeatureSwitchesWithOverrides();
  const { holdoutData } = useEnsureFeatureSwitches();

  // prevent unnecessary re-renders

  const logStartExperiment = useCallback(
    (experiment: string) => {
      options.logStartExperiment(experiment);
    },
    [options],
  );

  const logEvent = useCallback(
    (event: { eventName: string; eventValue: string; metadata: Record<string, string> }) => {
      options.logEvent(event);
    },
    [options],
  );

  const calculateVariantAndStartExperiment = useCallback(() => {
    const clientFilterResult = options.clientExcludeUser?.(data, experiment) || false;
    const manualOverride = featureSwitchesConfiguration?.useFeatureSwitchOverridesHook?.()?.[experiment];

    return _startExperiment({
      experiment,
      data,
      holdoutData: holdoutData ?? {},
      isInAudience: isInAudience && !clientFilterResult,
      logStartExperiment,
      logEvent,
      manualOverride: manualOverride as string | undefined,
    });
  }, [experiment, isInAudience, logStartExperiment, logEvent, options, data, holdoutData]);

  const variant = calculateVariantAndStartExperiment();

  return variant;
};

export const useLazyStartExperimentWithHoldout = <T extends FeatureSwitchName>(
  experiment: T,
  options: StartExperimentWhenInAudienceOptions<T>,
): (({ isInAudience }: { isInAudience: boolean }) => string) => {
  const data = useFetchedFeatureSwitchesWithOverrides();
  const { holdoutData } = useEnsureFeatureSwitches();
  const manualOverride = featureSwitchesConfiguration?.useFeatureSwitchOverridesHook?.()?.[experiment];

  const logStartExperiment = useCallback(
    (experiment: string) => {
      options.logStartExperiment(experiment);
    },
    [options],
  );
  const logEvent = useCallback(
    (event: { eventName: string; eventValue: string; metadata: Record<string, string> }) => {
      options.logEvent(event);
    },
    [options],
  );

  const calculateVariantAndStartExperiment = useCallback(
    ({ isInAudience }: { isInAudience: boolean }) => {
      const clientExcludeUser = options.clientExcludeUser?.(data, experiment) || false;

      return _startExperiment({
        experiment,
        data,
        holdoutData: holdoutData ?? {},
        isInAudience: isInAudience && !clientExcludeUser,
        logStartExperiment,
        logEvent,
        manualOverride: manualOverride as string | undefined,
      });
    },
    [experiment, logStartExperiment, logEvent, options, data, holdoutData, manualOverride],
  );

  return calculateVariantAndStartExperiment;
};

const STICKY_ASSIGNMENT_PREFIX = "sticky-assignment-";

// Decides whether a sticky experiment may use its cached arm (same flow as iOS):
// - Out of audience (incl. `clientExcludeUser`): not sticky, and the cache is left untouched.
// - A `useFeatureSwitchOverridesHook` override or a holdout result is final: not sticky, and the cache is cleared.
// - Otherwise the live arm is sticky. A cached arm is used only while the switch isn't "off", so a
//   ramp-down returns "off" but keeps the cached arm.
function getStickyState<T extends FeatureSwitchName>({
  experiment,
  data,
  holdoutData,
  manualOverride,
  isInAudience,
  clientExcludeUser,
  storageKey,
}: {
  experiment: T;
  data: Record<T, string>;
  holdoutData: ReturnType<typeof useEnsureFeatureSwitches>["holdoutData"];
  manualOverride: string | undefined;
  isInAudience: boolean;
  clientExcludeUser: StartExperimentWhenInAudienceOptions<T>["clientExcludeUser"];
  storageKey: string;
}) {
  const inAudience = isInAudience && !(clientExcludeUser?.(data, experiment) ?? false);
  const isFinal = !!manualOverride || isHoldoutResult(experiment, data, holdoutData);
  const cachedVariant = inAudience && !isFinal ? readCachedArm(storageKey, data[experiment]) : null;
  return { inAudience, isFinal, cachedVariant };
}

// Whether the holdout decides this user's variant: the experiment shipped, or they're held out of it.
// Keep in sync with the holdout branches in `_startExperiment`.
function isHoldoutResult(
  experiment: string,
  data: Record<string, string>,
  holdoutData: ReturnType<typeof useEnsureFeatureSwitches>["holdoutData"],
): boolean {
  const holdout = holdoutData?.[experiment];
  if (!holdout) return false;
  return holdout.status === "shipped" || isHoldoutVariant(data[holdout.holdoutName]);
}

// The cached arm, bypassed while the switch is "off" so a ramp-down takes effect without losing it.
function readCachedArm(storageKey: string, switchValue: string | undefined): string | null {
  if (!switchValue || switchValue === "off") return null;
  const cached = localStorage.getItem(storageKey);
  return cached !== null && cached !== "off" ? cached : null;
}

// Persists the outcome once the variant is resolved: clear on a final result, cache a new live arm.
function updateStickyCache(
  storageKey: string,
  { inAudience, isFinal, cachedVariant }: ReturnType<typeof getStickyState>,
  variant: string,
) {
  if (!inAudience || cachedVariant) return;
  if (isFinal) localStorage.removeItem(storageKey);
  else if (variant !== "off") localStorage.setItem(storageKey, variant);
}

function narrowOverride(override: unknown): string | undefined {
  return typeof override === "string" ? override : undefined;
}

export type StartExperimentWithStickyAssignmentOptions<T extends FeatureSwitchName> =
  StartExperimentWhenInAudienceOptions<T> & {
    /**
     * The current entity's id. Folded into the localStorage key so a shared device (e.g. a family
     * tablet with multiple parent logins) doesn't leak one entity's sticky variant to another.
     */
    entityId: string;
    /**
     * Called when returning a sticky cached variant. Wraps logClient.logStartExperimentWithOverrides
     * with the caller's entityId and entityType so the log record uses the cached arm, not whatever
     * variant the API is currently returning.
     */
    logStartExperimentWithOverrides: (experiment: string, variant: string) => void;
  };

/**
 * Like `useStartExperimentWithHoldout`, but persists the first assigned variant in localStorage
 * so the user sees the same variant on every subsequent render — even as experiment weights shift.
 *
 * If the feature switch value is "off" (ramp-down), "off" is returned and the cached arm is kept.
 * A `useFeatureSwitchOverridesHook` (DevTools / URL) override or a holdout result wins over the
 * cached arm and clears it. Only non-"off" assignments are cached; users not in audience never
 * touch the cache and can be enrolled later.
 */
export const useStartExperimentWithStickyAssignment = <T extends FeatureSwitchName>(
  experiment: T,
  { isInAudience }: { isInAudience: boolean },
  options: StartExperimentWithStickyAssignmentOptions<T>,
): FeatureSwitchVariants<T> => {
  const storageKey = `${STICKY_ASSIGNMENT_PREFIX}${experiment}-${options.entityId}`;
  const data = useFetchedFeatureSwitchesWithOverrides<string>();
  const { holdoutData } = useEnsureFeatureSwitches();
  const manualOverride = narrowOverride(featureSwitchesConfiguration?.useFeatureSwitchOverridesHook?.()?.[experiment]);
  const stickyState = getStickyState({
    experiment,
    data,
    holdoutData,
    manualOverride,
    isInAudience,
    clientExcludeUser: options.clientExcludeUser,
    storageKey,
  });

  // A ref, not a dependency, so an unmemoized inline `options.logStartExperimentWithOverrides`
  // from the caller doesn't retrigger the effect below on every render.
  const logStartExperimentWithOverridesRef = useRef(options.logStartExperimentWithOverrides);
  logStartExperimentWithOverridesRef.current = options.logStartExperimentWithOverrides;

  // Always call the underlying hook (React rules forbid conditional hook calls).
  // Suppress isInAudience when we have a cache so the hook doesn't log a shifted variant.
  const apiVariant = useStartExperimentWithHoldout(
    experiment,
    { isInAudience: stickyState.inAudience && !stickyState.cachedVariant },
    options,
  );

  // Logs a cached arm's START and writes or clears the cache. Runs in an effect so these side
  // effects only happen after a committed render — concurrent mode can discard renders mid-flight,
  // so writes in the render body could persist state that React never actually committed.
  const { inAudience, isFinal, cachedVariant } = stickyState;
  useEffect(() => {
    if (cachedVariant) {
      logStartExperimentWithOverridesRef.current(experiment, cachedVariant);
    }
    updateStickyCache(storageKey, { inAudience, isFinal, cachedVariant }, apiVariant);
  }, [cachedVariant, apiVariant, inAudience, isFinal, storageKey, experiment]);

  return (cachedVariant ?? apiVariant) as FeatureSwitchVariants<T>;
};

/**
 * Like `useLazyStartExperimentWithHoldout`, but persists the first assigned variant in localStorage
 * so the user sees the same variant on every call — even as experiment weights shift.
 *
 * Returns a callback that accepts `{ isInAudience }`. Call it from an event handler or effect,
 * not during render. The localStorage write is safe inside the callback since it runs outside
 * React's render phase. Same holdout, override and cache rules as `useStartExperimentWithStickyAssignment`.
 */
export const useLazyStartExperimentWithStickyAssignment = <T extends FeatureSwitchName>(
  experiment: T,
  options: StartExperimentWithStickyAssignmentOptions<T>,
): (({ isInAudience }: { isInAudience: boolean }) => string) => {
  const storageKey = `${STICKY_ASSIGNMENT_PREFIX}${experiment}-${options.entityId}`;
  const data = useFetchedFeatureSwitchesWithOverrides<string>();
  const { holdoutData } = useEnsureFeatureSwitches();
  const manualOverride = narrowOverride(featureSwitchesConfiguration?.useFeatureSwitchOverridesHook?.()?.[experiment]);
  const { logStartExperiment, logEvent, logStartExperimentWithOverrides, clientExcludeUser } = options;

  return useCallback(
    ({ isInAudience }: { isInAudience: boolean }) => {
      const stickyState = getStickyState({
        experiment,
        data,
        holdoutData,
        manualOverride,
        isInAudience,
        clientExcludeUser,
        storageKey,
      });
      const { inAudience, cachedVariant } = stickyState;

      if (cachedVariant) {
        logStartExperimentWithOverrides(experiment, cachedVariant);
        return cachedVariant;
      }

      const apiVariant = _startExperiment({
        experiment,
        data,
        holdoutData: holdoutData ?? {},
        isInAudience: inAudience,
        logStartExperiment,
        logEvent,
        manualOverride,
      });

      updateStickyCache(storageKey, stickyState, apiVariant);
      return apiVariant;
    },
    [
      experiment,
      storageKey,
      logStartExperiment,
      logEvent,
      logStartExperimentWithOverrides,
      clientExcludeUser,
      data,
      holdoutData,
      manualOverride,
    ],
  );
};

// ---------------------------
// HELPERS
//
export function _testOnlyResetFeatureSwitchesConfig() {
  if (!isTesting) throw new Error("`_testOnlyResetFeatureSwitchesConfig` can only be called in tests");

  featureSwitchesConfiguration = null;
  storedFeatureSetId = undefined;
  // Otherwise `getResolvedFeatureSwitches()` keeps reporting the previous test's switches.
  globalResolvedFeatureSwitchesCache = undefined;

  if (FEATURE_SET_ID_LOCALSTORAGE_KEY) {
    localStorage.removeItem(FEATURE_SET_ID_LOCALSTORAGE_KEY);
  }
}
